{
  "app": {
    "name": "ClairoHealth",
    "tagline": "Home care, made clear."
  },
  "common": {
    "cancel": "Cancel",
    "continue": "Continue",
    "back": "Back",
    "save": "Save",
    "close": "Close",
    "copy": "Copy",
    "copied": "Copied",
    "retry": "Retry",
    "undo": "Undo",
    "loading": "Loading…",
    "comingSoon": "Coming soon",
    "search": "Search",
    "new": "New",
    "optional": "Optional",
    "soonShort": "Soon"
  },
  "boot": {
    "lines": ["Lighting the beacon…", "Checking the harbour…", "Getting your day ready…"],
    "failed": "We couldn't start ClairoHealth. Check your connection and try again."
  },
  "theme": {
    "title": "Appearance",
    "subtitle": "Pick a look that suits your day. Changes apply instantly.",
    "button": "Change theme",
    "current": "Current theme: {{name}}",
    "system": {
      "name": "System",
      "mood": "Follows your device: Lighthouse by day, Midnight Tide by night."
    },
    "lighthouse": {
      "name": "Lighthouse",
      "mood": "Calm and trustworthy. Navy and beam-amber on warm paper."
    },
    "harbour-dawn": {
      "name": "Harbour Dawn",
      "mood": "Fresh morning light. Deep teal with coral highlights."
    },
    "iceberg-alley": {
      "name": "Iceberg Alley",
      "mood": "Crisp and airy. Glacier blue on cool white."
    },
    "jellybean-row": {
      "name": "Jellybean Row",
      "mood": "Playful and bright, like St. John's row houses."
    },
    "fogbank": {
      "name": "Fogbank",
      "mood": "Soft and focused. Muted slate for long desk days."
    },
    "northern-lights": {
      "name": "Northern Lights",
      "mood": "Rich and immersive. Indigo with aurora greens and violets."
    },
    "midnight-tide": {
      "name": "Midnight Tide",
      "mood": "True dark and easy on the eyes. Navy-black with amber."
    },
    "light": "Light",
    "dark": "Dark",
    "auto": "Auto"
  },
  "language": {
    "label": "Language",
    "en": "English",
    "fr": "Français",
    "short": {
      "en": "EN",
      "fr": "FR"
    }
  },
  "auth": {
    "hero": [
      "Care that reaches every doorstep.",
      "Schedules, notes and teams, in one clear place.",
      "Built in Newfoundland for Canadian home care."
    ],
    "signIn": {
      "title": "Welcome back",
      "subtitle": "Sign in to your ClairoHealth workspace.",
      "email": "Work email",
      "password": "Password",
      "submit": "Sign in",
      "passkey": "Sign in with a passkey",
      "or": "or",
      "forgot": "Forgot password?",
      "invited": "First time here? Use the temporary password from your invitation email."
    },
    "newPassword": {
      "title": "Choose your password",
      "subtitle": "Replace your temporary password with one only you know.",
      "password": "New password",
      "confirm": "Confirm password",
      "rule": "At least 14 characters. A short sentence works well.",
      "mismatch": "The passwords don't match.",
      "tooShort": "Use at least 14 characters.",
      "submit": "Set password and continue"
    },
    "totp": {
      "title": "Two-step verification",
      "subtitle": "Enter the 6-digit code from your authenticator app.",
      "submit": "Verify"
    },
    "errors": {
      "invalidCredentials": "That email and password don't match. Check them and try again.",
      "tooManyAttempts": "Too many attempts. Wait a few minutes, then try again.",
      "codeMismatch": "That code didn't work. Codes change every 30 seconds — try the newest one.",
      "sessionExpired": "That took too long and the sign-in expired. Please start again.",
      "passkeyUnavailable": "Passkeys aren't available on this device or browser.",
      "passwordPolicy": "That password doesn't meet the requirements.",
      "generic": "Something went wrong while signing in. Please try again.",
      "locked": "Your account is locked after too many failed sign-ins. Ask your administrator to unlock it.",
      "resetRequired": "You need to set a new password. Check your email for a code.",
      "breached": "This password has appeared in a data breach. Please choose a different one."
    },
    "signedOut": "You're signed out.",
    "preview": {
      "visitTitle": "Margaret O.",
      "visitBody": "09:00 · Visit complete",
      "noteTitle": "AI visit note",
      "noteBody": "Drafted in 4 s · ready to review",
      "teamTitle": "Avalon team",
      "teamBody": "12 caregivers on shift"
    },
    "forgot": {
      "title": "Reset your password",
      "subtitle": "We'll email you a code to set a new password.",
      "send": "Email me a code",
      "back": "Back to sign in"
    },
    "reset": {
      "title": "Set a new password",
      "subtitle": "Enter the code we emailed to {{email}}, then choose a new password.",
      "code": "Code from your email",
      "resend": "Send a new code",
      "resent": "We sent a new code to {{email}}.",
      "submit": "Save new password",
      "done": "Password updated. Sign in with your new password."
    }
  },
  "mfa": {
    "setup": {
      "title": "Protect your account",
      "reasonPlatform": "ClairoHealth staff accounts always use two-step verification.",
      "reasonOrganization": "Your organization requires two-step verification.",
      "step1": "Scan this code with an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password.",
      "step2": "Enter the 6-digit code the app shows.",
      "manualKey": "Can't scan? Enter this key instead",
      "verify": "Turn on two-step verification",
      "verifying": "Checking your code…",
      "success": "Two-step verification is on. You're all set.",
      "preparing": "Preparing a secure code…"
    },
    "banner": {
      "title": "Two-step verification is required",
      "body_one": "Set it up within {{count}} day to keep access.",
      "body_other": "Set it up within {{count}} days to keep access.",
      "action": "Set it up now"
    },
    "passkey": {
      "title": "Add a passkey",
      "body": "Sign in with Face ID, Touch ID or Windows Hello — faster and phishing-proof.",
      "action": "Add a passkey",
      "adding": "Waiting for your device…",
      "added": "Passkey added. Next time, just use your face or fingerprint."
    }
  },
  "shell": {
    "skip": "Skip to content",
    "nav": {
      "label": "Main",
      "home": "Home",
      "organization": "Organization",
      "schedule": "Schedule",
      "clients": "Clients",
      "caregivers": "Caregivers",
      "appearance": "Appearance",
      "people": "People"
    },
    "collapse": "Collapse sidebar",
    "expand": "Expand sidebar",
    "orgSwitcher": {
      "label": "Organization",
      "title": "Switch organization",
      "none": "No organization",
      "platform": "Platform"
    },
    "palette": {
      "open": "Search or jump to…",
      "placeholder": "Type a command or search…",
      "empty": "No results.",
      "navigate": "Go to",
      "appearance": "Appearance",
      "language": "Language",
      "account": "Account"
    },
    "user": {
      "menu": "Account menu",
      "security": "Security",
      "signOut": "Sign out"
    }
  },
  "home": {
    "greeting": {
      "morning": "Good morning, {{name}}",
      "afternoon": "Good afternoon, {{name}}",
      "evening": "Good evening, {{name}}"
    },
    "today": "{{date}} · {{zone}}",
    "stats": {
      "orgUnits": "Org units",
      "branches": "Branches",
      "role": "Your role",
      "security": "Account security",
      "mfaOn": "2-step on",
      "mfaOff": "2-step off"
    },
    "checklist": {
      "title": "Getting started",
      "progress": "{{done}} of {{total}} done",
      "mfa": "Turn on two-step verification",
      "passkey": "Add a passkey for one-tap sign-in",
      "theme": "Make it yours: pick a theme",
      "invite": "Invite your team",
      "clients": "Add your first clients"
    },
    "orgCard": {
      "title": "Your organization",
      "manage": "Manage"
    },
    "platform": {
      "title": "Platform console",
      "body": "You're signed in as ClairoHealth staff without an agency. Agency onboarding lands here next."
    }
  },
  "org": {
    "title": "Organization",
    "subtitle": "Regions, branches and teams. Access flows down the tree and stops at custodians.",
    "add": "Add org unit",
    "empty": "Nothing here yet.",
    "custodianBadge": "Custodian",
    "dialog": {
      "title": "Add an org unit",
      "name": "Name",
      "namePlaceholder": "e.g. Gander branch",
      "type": "Type",
      "parent": "Inside",
      "province": "Province",
      "custodian": "Separate health information custodian",
      "custodianHint": "Its own privacy boundary: parent organizations won't see its records.",
      "submit": "Add"
    },
    "types": {
      "agency": "Agency",
      "region": "Region",
      "branch": "Branch",
      "team": "Team",
      "franchisor": "Franchisor",
      "franchisee": "Franchisee",
      "household": "Household"
    },
    "toast": {
      "creating": "Adding {{name}}…",
      "created": "{{name}} added",
      "createdBody": "It's ready for people and settings."
    }
  },
  "roles": {
    "owner": "Owner",
    "admin": "Admin",
    "coordinator": "Coordinator",
    "care_manager": "Care manager",
    "billing": "Billing",
    "caregiver": "Caregiver",
    "platform_admin": "Platform admin"
  },
  "errors": {
    "unauthenticated": "Your session ended. Please sign in again.",
    "forbidden": "You don't have access to do that.",
    "not_found": "We couldn't find that.",
    "conflict": "That already exists.",
    "bad_request": "Some details need another look.",
    "mfa_enrollment_required": "Set up two-step verification to continue.",
    "internal": "Something went wrong on our side. We're on it.",
    "network": "Can't reach ClairoHealth. Check your connection.",
    "copyReference": "Copy reference",
    "referenceCopied": "Reference copied",
    "reference": "Reference {{id}}"
  },
  "notFound": {
    "title": "Lost at sea",
    "body": "This page drifted off. Let's get you back to shore.",
    "home": "Back to home"
  },
  "session": {
    "idle": {
      "title": "Still there?",
      "body": "For your security, you'll be signed out in {{seconds}} seconds.",
      "stay": "Stay signed in",
      "signedOut": "You were signed out after {{minutes}} minutes without activity."
    }
  },
  "people": {
    "title": "People",
    "subtitle": "Everyone with access to this organization, and the security of their accounts.",
    "empty": "No one here yet.",
    "you": "You",
    "staff": "ClairoHealth staff",
    "columns": {
      "person": "Person",
      "access": "Access",
      "security": "2-step",
      "account": "Account"
    },
    "states": {
      "active": "Active",
      "invited": "Invited",
      "reset_required": "Must reset password",
      "locked": "Locked",
      "unknown": "Unknown"
    },
    "failedSignIns_one": "{{count}} failed sign-in",
    "failedSignIns_other": "{{count}} failed sign-ins",
    "mfaOn": "On",
    "mfaOff": "Off",
    "actions": {
      "menu": "Actions for {{name}}",
      "unlock": "Unlock and reset password",
      "signOut": "Sign out everywhere",
      "remove": "Remove from organization"
    },
    "remove": {
      "title": "Remove {{name}}?",
      "body": "They lose access to this organization right away. Their access to other organizations isn't affected.",
      "confirm": "Remove"
    },
    "toast": {
      "unlocking": "Unlocking {{name}}…",
      "unlocked": "{{name}} unlocked",
      "unlockedBody": "We emailed them a code to set a new password.",
      "signingOut": "Signing {{name}} out…",
      "signedOut": "{{name}} is signed out everywhere",
      "removing": "Removing {{name}}…",
      "removed": "{{name}} removed from this organization"
    }
  }
}
